/**
 * Debug helpers for inspecting stored audio blobs
 * Exposes functions to window for console debugging
 */

import { getAllDrafts, getSubmissions } from './db'
import { detectAudioContainerFromBytes, getAudioContainerFromMimeType } from './audio'

export interface BlobInspectionResult {
  id: string
  type: 'draft' | 'submission'
  blobType: string | null
  blobSize: number | null
  canRead: boolean
  isValid: boolean
  headerString?: string
  headerHex?: string
  error?: string
}

/**
 * Inspect a single blob's integrity
 */
async function inspectBlob(blob: Blob | null | undefined): Promise<{
  canRead: boolean
  isValid: boolean
  size: number
  type: string
  declaredContainer?: string
  detectedContainer?: string
  headerString?: string
  headerHex?: string
  error?: string
}> {
  if (!blob) {
    return {
      canRead: false,
      isValid: false,
      size: 0,
      type: 'null',
      error: 'Blob is null or undefined',
    }
  }

  if (blob.size === 0) {
    return {
      canRead: false,
      isValid: false,
      size: 0,
      type: blob.type || 'unknown',
      error: 'Blob has zero size',
    }
  }

  try {
    const arrayBuffer = await blob.arrayBuffer()
    if (arrayBuffer.byteLength === 0) {
      return {
        canRead: false,
        isValid: false,
        size: blob.size,
        type: blob.type || 'unknown',
        error: 'Blob arrayBuffer is empty after read',
      }
    }

    const headerBytes = new Uint8Array(arrayBuffer.slice(0, 12))
    const headerString = String.fromCharCode(...headerBytes.slice(0, 4))
    const headerHex = Array.from(headerBytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join(' ')
    const declaredContainer = getAudioContainerFromMimeType(blob.type)
    const detectedContainer = detectAudioContainerFromBytes(headerBytes)
    const hasContainerMismatch =
      declaredContainer !== 'unknown' &&
      detectedContainer !== 'unknown' &&
      declaredContainer !== detectedContainer
    const isValid = !hasContainerMismatch && arrayBuffer.byteLength > 0

    return {
      canRead: true,
      isValid,
      size: blob.size,
      type: blob.type || 'unknown',
      declaredContainer,
      detectedContainer,
      headerString,
      headerHex,
      error: hasContainerMismatch
        ? `Container mismatch: declared ${declaredContainer}, detected ${detectedContainer}`
        : undefined,
    }
  } catch (error) {
    return {
      canRead: false,
      isValid: false,
      size: blob.size,
      type: blob.type || 'unknown',
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

/**
 * Inspect all stored drafts and submissions
 */
export async function inspectAllStoredBlobs(): Promise<{
  drafts: BlobInspectionResult[]
  submissions: BlobInspectionResult[]
  summary: {
    totalDrafts: number
    totalSubmissions: number
    corruptedDrafts: number
    corruptedSubmissions: number
    emptyDrafts: number
    emptySubmissions: number
  }
}> {
  console.log('[DEBUG] Starting blob inspection...')

  const drafts = await getAllDrafts()
  const submissions = await getSubmissions()

  console.log(`[DEBUG] Found ${drafts.length} drafts and ${submissions.length} submissions`)

  const draftResults: BlobInspectionResult[] = []
  const submissionResults: BlobInspectionResult[] = []

  // Inspect drafts
  for (const draft of drafts) {
    const inspection = await inspectBlob(draft.blob)
    draftResults.push({
      id: draft.id,
      type: 'draft',
      blobType: draft.blob?.type || null,
      blobSize: draft.blob?.size || null,
      canRead: inspection.canRead,
      isValid: inspection.isValid,
      headerString: inspection.headerString,
      headerHex: inspection.headerHex,
      error: inspection.error,
    })
  }

  // Inspect submissions
  for (const submission of submissions) {
    if (submission.type !== 'audio') continue

    const localBlob = submission.localBlob as Blob | undefined | null
    const inspection = await inspectBlob(localBlob)
    submissionResults.push({
      id: submission.id,
      type: 'submission',
      blobType: localBlob?.type || null,
      blobSize: localBlob?.size || null,
      canRead: inspection.canRead,
      isValid: inspection.isValid,
      headerString: inspection.headerString,
      headerHex: inspection.headerHex,
      error: inspection.error,
    })
  }

  const summary = {
    totalDrafts: drafts.length,
    totalSubmissions: submissions.filter((s) => s.type === 'audio').length,
    corruptedDrafts: draftResults.filter((r) => !r.canRead || !r.isValid).length,
    corruptedSubmissions: submissionResults.filter((r) => !r.canRead || !r.isValid).length,
    emptyDrafts: draftResults.filter((r) => r.blobSize === 0).length,
    emptySubmissions: submissionResults.filter((r) => r.blobSize === 0).length,
  }

  console.log('[DEBUG] Blob inspection complete:', summary)
  console.log('[DEBUG] Draft results:', draftResults)
  console.log('[DEBUG] Submission results:', submissionResults)

  return {
    drafts: draftResults,
    submissions: submissionResults,
    summary,
  }
}

/**
 * Expose debug functions to window for console access
 */
if (typeof window !== 'undefined') {
  // @ts-expect-error - Adding debug functions to window
  window.inspectAllStoredBlobs = inspectAllStoredBlobs
  // @ts-expect-error - Adding debug functions to window
  window.getSubmissions = getSubmissions
  // @ts-expect-error - Adding debug functions to window
  window.getAllDrafts = getAllDrafts

  console.log(
    '[DEBUG] Blob inspection helpers available:',
    '\n  window.inspectAllStoredBlobs() - Inspect all stored blobs',
    '\n  window.getSubmissions() - Get all submissions',
    '\n  window.getAllDrafts() - Get all drafts',
  )
}
